import {Children, isValidElement, JSX, ReactNode} from "react";

import SmartImage from "@/shared/ui/smart-image";

import classNames from "classnames";

import {hasChildOfType} from "@/shared/utils/react";

import {HeroContent} from "./parts/hero-content";
import {HeroDescription} from "./parts/hero-description";
import {HeroHeader} from "./parts/hero-header";
import {HeroLogo} from "./parts/hero-logo";
import {HeroMarker} from "./parts/hero-marker";
import {HeroSearch} from "./parts/hero-search";
import {HeroSlider} from "./parts/hero-slider";
import {HeroTitle} from "./parts/hero-title";

import "./styles/hero.scss";

export type HeroProps = {
    image: {
        url?: string;
        alt?: string;
    };
    darkness?: boolean;
    type?: "default" | "franchise";
    children?: ReactNode;
    anchorId?: string;
};

function HeroRoot({image, darkness = true, type = "default", children, anchorId}: HeroProps): JSX.Element {
    const childArray = Children.toArray(children);
    const markerChild = childArray.filter((child) => isValidElement(child) && child.type === HeroMarker);
    const otherChildren = childArray.filter((child) => !(isValidElement(child) && child.type === HeroMarker));
    const hasTitle = hasChildOfType(otherChildren, [HeroContent, HeroHeader]);

    return (
        <section
            id={anchorId}
            data-search-z-index="search-z-index"
            className={classNames("hero", {
                "hero_darkness-no": !darkness,
                "hero_no-title": !hasTitle,
                [`hero_${type}`]: type !== "default",
            })}
        >
            {image.url && (
                <SmartImage
                    className="hero__background"
                    src={image.url}
                    alt={image.alt ?? ""}
                    fill
                    priority
                    sizes="100vw"
                    quality={85}
                    style={{objectFit: "cover"}}
                />
            )}

            <div className={classNames("hero__container")}>{otherChildren}</div>
            {markerChild}
        </section>
    );
}

type HeroComponent = typeof HeroRoot & {
    Search: typeof HeroSearch;
    Title: typeof HeroTitle;
    Description: typeof HeroDescription;
    Content: typeof HeroContent;
    Header: typeof HeroHeader;
    Logo: typeof HeroLogo;
    Slider: typeof HeroSlider;
    Marker: typeof HeroMarker;
};

export const Hero = Object.assign(HeroRoot, {
    Search: HeroSearch,
    Title: HeroTitle,
    Description: HeroDescription,
    Content: HeroContent,
    Header: HeroHeader,
    Logo: HeroLogo,
    Slider: HeroSlider,
    Marker: HeroMarker,
}) as HeroComponent;
